<template>
  <div class="loading-progress">
    <div class="progress-header mb-4">
      <h4 class="loading-title">{{ title }}</h4>
      <p class="loading-subtitle">{{ subtitle || currentStageText }}</p>
    </div>

    <div class="progress-container mb-4">
      <div class="progress" style="height: 8px;">
        <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
          :style="{ width: progressPercentage + '%' }" :aria-valuenow="progressPercentage" aria-valuemin="0"
          aria-valuemax="100"></div>
      </div>
      <div class="progress-text mt-2">
        <small class="text-muted">{{ progressPercentage }}% complete</small>
      </div>
    </div>

    <div class="loading-stages">
      <div v-for="(stage, index) in stages" :key="stage.key" class="stage-item" :class="{
        'stage-completed': completedStages.includes(stage.key),
        'stage-active': index === currentStageIndex && !completedStages.includes(stage.key),
        'stage-pending': index > currentStageIndex
      }">
        <div class="stage-icon">
          <i v-if="completedStages.includes(stage.key)" class="fa-solid fa-check"></i>
          <i v-else-if="index === currentStageIndex" class="fa-solid fa-spinner fa-spin"></i>
          <i v-else :class="stage.icon"></i>
        </div>
        <div class="stage-content">
          <div class="stage-title">{{ stage.title }}</div>
          <div class="stage-description">{{ stage.description }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoadingProgress',
  props: {
    currentStage: {
      type: String,
      default: 'config'
    },
    completedStages: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: 'Loading Your Budget'
    },
    subtitle: {
      type: String,
      default: ''
    },
    stages: {
      type: Array,
      default: () => [
        {
          key: 'config',
          title: 'User Configuration',
          description: 'Loading your preferences and settings',
          icon: 'fa-solid fa-cog'
        },
        {
          key: 'budget',
          title: 'Budget Data',
          description: 'Syncing your income and expenses',
          icon: 'fa-solid fa-wallet'
        },
        {
          key: 'savings-goals',
          title: 'Savings Goals',
          description: 'Loading your savings objectives',
          icon: 'fa-solid fa-piggy-bank'
        },
        {
          key: 'transactions',
          title: 'Transaction History',
          description: 'Loading your recent transactions',
          icon: 'fa-solid fa-receipt'
        },
        {
          key: 'keywords',
          title: 'Category Keywords',
          description: 'Setting up auto-categorization',
          icon: 'fa-solid fa-tags'
        }
      ]
    }
  },
  data() {
    return {
      // Default stages - can be overridden by props
    }
  },
  computed: {
    currentStageIndex() {
      return this.stages.findIndex(stage => stage.key === this.currentStage);
    },
    progressPercentage() {
      const totalStages = this.stages.length;
      const completedCount = this.completedStages.length;

      // If all stages are completed, return 100%
      if (completedCount >= totalStages) {
        return 100;
      }

      // Add 50% progress for the current active stage (if it exists and isn't completed)
      const isCurrentStageActive = this.currentStageIndex >= 0 && !this.completedStages.includes(this.currentStage);
      const currentProgress = isCurrentStageActive ? 0.5 : 0;

      return Math.round(((completedCount + currentProgress) / totalStages) * 100);
    },
    currentStageText() {
      const stage = this.stages.find(s => s.key === this.currentStage);
      return stage ? stage.description : 'Preparing your data...';
    }
  }
}
</script>

<style scoped>
.loading-progress {
  max-width: 500px;
  margin: 0 auto;
  text-align: center;
}

.loading-title {
  color: var(--light-text);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.loading-subtitle {
  color: var(--light-text-muted);
  margin-bottom: 0;
  font-size: 0.95rem;
}

.progress-container {
  margin-bottom: 2rem;
}

.progress {
  background-color: var(--nested-container-bg);
  border-radius: 8px;
  overflow: hidden;
}

.progress-bar {
  background-color: #36A2EB;
}

.progress-text {
  text-align: center;
}

.loading-stages {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
}

.stage-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid transparent;
}

.stage-completed {
  background: transparent;
  opacity: 0.7;
  border-color: transparent;
}

.stage-active {
  background: var(--nested-container-bg);
  border-color: #36A2EB;
}

.stage-pending {
  background: var(--nested-container-bg);
  opacity: 0.5;
  border-color: transparent;
}

.stage-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.stage-completed .stage-icon {
  background-color: #28a745 !important;
  color: white !important;
}

.stage-active .stage-icon {
  background-color: #36A2EB !important;
  color: white !important;
}

.stage-pending .stage-icon {
  background-color: var(--light-border) !important;
  color: var(--light-text-muted) !important;
}

.stage-content {
  flex: 1;
}

.stage-title {
  font-weight: 600;
  color: var(--light-text);
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
}

.stage-description {
  color: var(--light-text-muted);
  font-size: 0.85rem;
  line-height: 1.3;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .loading-progress {
    max-width: 100%;
    padding: 0 1rem;
  }

  .loading-stages {
    gap: 0.75rem;
  }

  .stage-item {
    padding: 0.5rem;
  }

  .stage-icon {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }

  .stage-title {
    font-size: 0.9rem;
  }

  .stage-description {
    font-size: 0.8rem;
  }
}

/* Dark mode support */
:global([data-theme]) .progress {
  background-color: rgba(255, 255, 255, 0.1);
}

:global([data-theme]) .stage-completed {
  background: transparent;
}

:global([data-theme]) .stage-active {
  background: rgba(255, 255, 255, 0.05);
  border-color: #36A2EB;
}

:global([data-theme]) .stage-pending {
  background: rgba(255, 255, 255, 0.05);
}
</style>